import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export type LicenseStatus = 'UNCHECKED' | 'VALID' | 'INVALID' | 'EXPIRED';

interface LicenseState {
    licenseStatus: LicenseStatus;
    licenseKey: string | null;
    licenseTier: 'Lifetime' | 'Annual' | null;
    expiryDate: string | null;
}

interface LicenseContextType extends LicenseState {
    validateLicense: (key: string) => Promise<boolean>;
    clearLicense: () => void;
}

const LicenseContext = createContext<LicenseContextType | undefined>(undefined);

// --- Mock License Server Validation ---
const mockValidateKeyOnServer = async (key: string): Promise<Omit<LicenseState, 'licenseKey'>> => {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 800));

    switch (key) {
        case 'LIFETIME-VALID-1997':
            return { licenseStatus: 'VALID', licenseTier: 'Lifetime', expiryDate: null };
        case 'ANNUAL-VALID-1097':
            return { licenseStatus: 'VALID', licenseTier: 'Annual', expiryDate: '2025-12-31' };
        case 'ANNUAL-EXPIRED-0000':
            return { licenseStatus: 'EXPIRED', licenseTier: 'Annual', expiryDate: '2023-12-31' };
        default:
            return { licenseStatus: 'INVALID', licenseTier: null, expiryDate: null };
    }
};

export const LicenseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, setState] = useState<LicenseState>({
        licenseStatus: 'VALID',
        licenseKey: 'SELF-HOSTED',
        licenseTier: 'Lifetime',
        expiryDate: null,
    });

    const clearLicense = useCallback(() => {
        localStorage.removeItem('cms_license_key');
        localStorage.removeItem('cms_license_status');
        setState({
            licenseStatus: 'INVALID',
            licenseKey: null,
            licenseTier: null,
            expiryDate: null,
        });
        // The /admin/activate route is commented out, so navigating here causes a crash.
        // This function should only clear state.
    }, []);

    const validateLicense = useCallback(async (key: string): Promise<boolean> => {
        const result = await mockValidateKeyOnServer(key);

        if (result.licenseStatus === 'VALID' || result.licenseStatus === 'EXPIRED') {
            localStorage.setItem('cms_license_key', key);
            localStorage.setItem('cms_license_status', JSON.stringify(result));
            setState({
                licenseKey: key,
                ...result,
            });
            return true;
        } else {
            clearLicense();
            return false;
        }
    }, [clearLicense]);

    useEffect(() => {
        const storedKey = localStorage.getItem('cms_license_key');
        const storedStatus = localStorage.getItem('cms_license_status');

        if (storedKey && storedStatus) {
            try {
                const parsedStatus = JSON.parse(storedStatus);
                setState({
                    licenseKey: storedKey,
                    licenseStatus: parsedStatus.licenseStatus,
                    licenseTier: parsedStatus.licenseTier,
                    expiryDate: parsedStatus.expiryDate,
                });
            } catch (e) {
                // If parsing fails, just clear storage and set state without navigating.
                localStorage.removeItem('cms_license_key');
                localStorage.removeItem('cms_license_status');
                setState({
                    licenseStatus: 'INVALID',
                    licenseKey: null,
                    licenseTier: null,
                    expiryDate: null,
                });
            }
        } else {
            // The user has disabled the license activation flow.
            // Default to a valid state to allow the app to work without activation.
            setState({
                licenseStatus: 'VALID',
                licenseKey: 'DEV-BYPASS-KEY',
                licenseTier: 'Lifetime',
                expiryDate: null,
            });
        }
    }, []); // This effect should only run once on mount.

    return (
        <LicenseContext.Provider value={{ ...state, validateLicense, clearLicense }}>
            {children}
        </LicenseContext.Provider>
    );
};

export const useLicense = () => {
    const context = useContext(LicenseContext);
    if (context === undefined) {
        throw new Error('useLicense must be used within a LicenseProvider');
    }
    return context;
};

